import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { session } from "../auth/session";
import { i18n } from "../i18n";
import type { ModuleName } from "../router";
import "./product-taskbar";
import "./a2k-icon/a2k-icon";
import "./app-dialog";
import { api } from "../api/client";

const nav: Array<[ModuleName, string]> = [
  ["overview", "documents-icon"],
  ["knowledge", "documents-icon"],
  ["rag", "network-icon"],
  ["jobs", "info-icon"],
  ["logs", "help-icon"],
  ["keys", "network-icon"],
  ["users", "windows-icon"],
  ["codex", "internet-icon"],
  ["skills", "documents-icon"],
  ["sources", "network-icon"],
  ["settings", "windows-icon"],
];

@customElement("app-shell")
export class AppShell extends LitElement {
  @property() active: ModuleName = "overview";
  @property({ attribute: false }) content: TemplateResult | unknown = null;
  @state() private menuOpen = false;
  @state() private accountDialog: "profile" | "password" | null = null;
  @state() private accountBusy = false;
  @state() private accountError = "";
  @state() private profileUsername = "";
  @state() private profileEmail = "";
  @state() private currentPassword = "";
  @state() private newPassword = "";
  @state() private newPasswordConfirm = "";

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("sessionchange", this.languageChanged);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("sessionchange", this.languageChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();

  private select(id: ModuleName) {
    this.menuOpen = false;
    this.dispatchEvent(
      new CustomEvent("navigate", { detail: id, bubbles: true }),
    );
  }

  private closeMobileMenu() {
    this.menuOpen = false;
  }

  private openAccount(action: "profile" | "password") {
    this.accountError = "";
    this.accountDialog = action;
    this.profileUsername = session.user?.username || "";
    this.profileEmail = session.user?.email || "";
    this.currentPassword = this.newPassword = this.newPasswordConfirm = "";
  }

  private async saveAccount() {
    this.accountBusy = true;
    this.accountError = "";
    try {
      if (this.accountDialog === "profile") {
        const { user } = await api.updateProfile({
          username: this.profileUsername,
          email: this.profileEmail,
        });
        session.set(user);
      } else {
        await api.changePassword({
          current_password: this.currentPassword,
          new_password: this.newPassword,
          new_password_confirm: this.newPasswordConfirm,
        });
      }
      this.accountDialog = null;
    } catch (error) {
      this.accountError = (error as Error).message;
    } finally {
      this.accountBusy = false;
    }
  }

  private renderRouteContext() {
    if (this.active === "overview") return null;

    return html`
      <a2k-panel class="route-context">
        <div class="route-context-inner">
          <a2k-button size="small" @click=${() => this.select("overview")}>
            <span aria-hidden="true">←</span>
            <span class="back-label">${i18n.t("backToOverview")}</span>
          </a2k-button>
          <span class="breadcrumb-separator" aria-hidden="true">/</span>
          <nav class="breadcrumbs" aria-label="Breadcrumb">
            <span aria-current="page">${i18n.t(this.active)}</span>
          </nav>
        </div>
      </a2k-panel>
    `;
  }

  render() {
    return html`
      <a2k-desktop class="app-desktop">
        <div class="app-frame">
          <header class="topbar">
            <div class="brand-lockup">
              <span class="brand-mark">CJ</span>
              <span>
                <strong>CANGJIE CAMPAT</strong>
                <small>${i18n.t("shellSubtitle")}</small>
              </span>
            </div>
            <div class="context">
              <span class="health-dot"></span>
              <span>${i18n.t("operational")}</span>
              <span class="account">${session.user?.username || "—"}</span>
              <a2k-button size="small" @click=${() => i18n.toggle()}>
                ${i18n.t("language")}
              </a2k-button>
              <a2k-button
                class="mobile-menu"
                size="small"
                aria-expanded=${String(this.menuOpen)}
                aria-controls="console-navigation"
                @click=${() => (this.menuOpen = !this.menuOpen)}
              >
                ${i18n.t("menu")}
              </a2k-button>
            </div>
          </header>
          <aside
            id="console-navigation"
            class="sidebar ${this.menuOpen ? "is-open" : ""}"
            aria-label="Console navigation"
          >
            ${nav
              .filter(
                ([id]) =>
                  id !== "settings" || session.user?.role === "administrator",
              )
              .map(
                ([id, icon]) => html`
                  <button
                    class="nav-link ${id === this.active ? "is-active" : ""}"
                    data-module=${id}
                    @click=${() => this.select(id)}
                  >
                    <a2k-icon icon=${icon}></a2k-icon>
                    <span>${i18n.t(id)}</span>
                  </button>
                `,
              )}
            <a class="sidebar-docs" href="/api-docs">${i18n.t("docs")}</a>
          </aside>
          ${
            this.menuOpen
              ? html`
                  <button
                    class="mobile-menu-backdrop"
                    aria-label="Close navigation"
                    @click=${this.closeMobileMenu}
                  ></button>
                `
              : null
          }
          <main class="workspace" @click=${this.closeMobileMenu}>
            ${this.renderRouteContext()} ${this.content}
          </main>
        </div>
        <cpl-taskbar
          .active=${this.active}
          @logout=${() =>
            this.dispatchEvent(new Event("logout", { bubbles: true }))}
          @taskbar-profile=${() => this.openAccount("profile")}
          @taskbar-password=${() => this.openAccount("password")}
        ></cpl-taskbar>
        <app-dialog
          .open=${this.accountDialog !== null}
          heading=${this.accountDialog === "password" ? i18n.t("changePassword") : i18n.t("profile")}
          confirm-label=${i18n.t("save")}
          cancel-label=${i18n.t("cancel")}
          working-label=${i18n.t("working")}
          .busy=${this.accountBusy}
          .message=${this.accountError}
          @dialog-cancel=${() => (this.accountDialog = null)}
          @dialog-confirm=${this.saveAccount}
        >
          ${
            this.accountDialog === "password"
              ? html`
                  <label class="native-field account-field"
                    ><span class="field-label"
                      ><a2k-icon icon="key98-icon"></a2k-icon
                      >${i18n.t("currentPassword")}</span
                    ><input
                      class="a2k-native-input"
                      type="password"
                      .value=${this.currentPassword}
                      @input=${(event: Event) => (this.currentPassword = (event.target as HTMLInputElement).value)}
                  /></label>
                  <label class="native-field account-field"
                    ><span class="field-label"
                      ><a2k-icon icon="key98-icon"></a2k-icon
                      >${i18n.t("newPassword")}</span
                    ><input
                      class="a2k-native-input"
                      type="password"
                      minlength="8"
                      .value=${this.newPassword}
                      @input=${(event: Event) => (this.newPassword = (event.target as HTMLInputElement).value)}
                  /></label>
                  <label class="native-field account-field"
                    ><span class="field-label"
                      ><a2k-icon icon="key98-icon"></a2k-icon
                      >${i18n.t("registerConfirmPassword")}</span
                    ><input
                      class="a2k-native-input"
                      type="password"
                      minlength="8"
                      .value=${this.newPasswordConfirm}
                      @input=${(event: Event) => (this.newPasswordConfirm = (event.target as HTMLInputElement).value)}
                  /></label>
                `
              : html`
                  <label class="native-field account-field"
                    ><span class="field-label"
                      ><a2k-icon icon="user98-icon"></a2k-icon
                      >${i18n.t("loginUsername")}</span
                    ><input
                      class="a2k-native-input"
                      .value=${this.profileUsername}
                      @input=${(event: Event) => (this.profileUsername = (event.target as HTMLInputElement).value)}
                  /></label>
                  <label class="native-field account-field"
                    ><span class="field-label"
                      ><a2k-icon icon="user98-icon"></a2k-icon
                      >${i18n.t("registerEmail")}</span
                    ><input
                      class="a2k-native-input"
                      type="email"
                      .value=${this.profileEmail}
                      @input=${(event: Event) => (this.profileEmail = (event.target as HTMLInputElement).value)}
                  /></label>
                `
          }
        </app-dialog>
      </a2k-desktop>
    `;
  }
}
